import React, { Component } from "react"
import { Form, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Label, Button, Input, FormFeedback, Alert, Progress } from "reactstrap"
import Select from "react-select"
import moment from 'moment'
import { graphql } from '@apollo/client/react/hoc'
import { AllCarModels, AllAgreementTypes } from "./Queries"
import { CreatePromotionTemplateMutation } from './Mutations'
import toTitleCase from "../Functions/toTitleCase"
import withApolloClient from '../withApolloClient'
import './AddPromotionModal.css'


const IntervalOptions = [
    { value: 'Day', label: 'Day' },
    { value: 'Week', label: 'Week' },
    { value: 'Month', label: 'Month' },
]

// Promotions can only expire on a Sunday, matching the check in pricing/mutations.py
const isSunday = (date) => !!date && new Date(date + "T00:00:00").getDay() === 0

class AddPromotionModal extends Component {
    constructor(props) {
        super(props)
        this.firstField = React.createRef()
        this.state = {
            input: {
                title: null,
                promoPrice: null,
                minimumPromoPrice: null,
                maximumPromoPrice: null,
                cycle: null,
                minCycle: null,
                maxCycle: null,
                interval: 1,
                intervalUnit: '',
                carYearMin: null,
                carYearMax: null,
                visibleToCustomers: false,
                startDate: null,
                expirationDate: null,
                isPercentage: "false",
                carModels: null,
                tagNames: "",
            },
            createAnother: false,
            loading: false,
            error: "",
            step: 1,
        }
    }

    stepOneError = () => {
        const { title, agreementType, carYearMin, carYearMax } = this.state.input

        if (!title || !title.trim()) return "Promotion Title is required before continuing."
        if (!agreementType) return "Agreement Type is required before continuing."
        if (carYearMin && carYearMax && parseInt(carYearMin) > parseInt(carYearMax)) {
            return "Minimum Car Year must be less than or equal to the Maximum Car Year."
        }
        return ""
    }

    stepTwoError = () => {
        const input = this.state.input

        if (!input.promoPrice || !input.minimumPromoPrice || !input.maximumPromoPrice
            || !input.cycle || !input.minCycle || !input.maxCycle
            || !input.interval || !input.intervalUnit
            || !input.startDate || !input.expirationDate) {
            return "Please provide all required fields, for creating a pricing template!"
        }
        if (input.minimumPromoPrice > input.promoPrice) return "Minimum Price must be less than the default Price"
        if (input.maximumPromoPrice < input.promoPrice) return "Maximum Price must be higher than the default Price"
        if (input.minCycle > input.cycle) return "Minimum Cycle must be less than the default Cycle"
        if (input.maxCycle < input.cycle) return "Maximum Cycle must be higher than the default Cycle"
        if (input.startDate > input.expirationDate) return "Expiration Date must be on or after the Start Date."
        if (!isSunday(input.expirationDate)) return "Expiration Date can only be a Sunday."
        return ""
    }

    handleSubmit = (e) => {
        e.preventDefault()

        // Enter on a Step 1 field submits the form, it should advance instead of creating the promo
        if (this.state.step === 1) {
            this.handleNext()
            return
        }

        let input = { ...this.state.input }

        if (input.tagNames && typeof input.tagNames === "string") {
            input.tagNames = input.tagNames.split(",").map(item => item.trim())
        }

        const validationError = this.stepOneError() || this.stepTwoError()
        if (validationError) {
            this.setState({ loading: false, error: validationError })
            return
        }

        try {
            input = {
                ...input, promoPrice: parseFloat(input.promoPrice),
                minimumPromoPrice: parseFloat(input.minimumPromoPrice), maximumPromoPrice: parseFloat(input.maximumPromoPrice),
                cycle: parseFloat(input.cycle), minCycle: parseFloat(input.minCycle),
                maxCycle: parseFloat(input.maxCycle), carYearMin: parseInt(input.carYearMin),
                carYearMax: parseInt(input.carYearMax),
                isPercentage: this.state.input.isPercentage === "true",
                interval: parseInt(input.interval),
                intervalUnit: input.intervalUnit.charAt(0).toUpperCase() + input.intervalUnit.substr(1).toLowerCase(),
            }

            this.setState({ loading: true })
            this.props.apolloClient.mutate({
                mutation: CreatePromotionTemplateMutation,
                variables: { input },
            }).then((result) => {
                if (result.data && result.data.createPromotionTemplate && result.data.createPromotionTemplate.ok) {
                    this.props.refetch()
                    this.props.toggleModal()
                    if (this.state.createAnother) {
                        setTimeout(() => {
                            this.props.toggleModal()
                        }, 200)
                    }
                    this.setState({ loading: false, error: "" })
                } else if (result.data && result.data.createPromotionTemplate && !result.data.createPromotionTemplate.ok && result.data.createPromotionTemplate.errors && result.data.createPromotionTemplate.errors[0]) {
                    this.setState({ loading: false, error: result.data.createPromotionTemplate.errors[0].messages.toString() })
                } else {
                    this.setState({ loading: false, error: "Something went wrong, please contact admin for support!" })
                }
            }).catch((err) => {
                this.setState({ loading: false, error: err.message })
            })
        } catch (err) {
            this.setState({ loading: false, error: err })
        }
    };

    handleChange = (e, number = false) => {
        if (e.target.type === "checkbox")
            this.setState({ input: { ...this.state.input, [e.target.name]: !this.state.input[e.target.name] } })
        else if (number) {
            this.setState({ input: { ...this.state.input, [e.target.name]: parseFloat(e.target.value) } })
        }
        else {
            this.setState({ input: { ...this.state.input, [e.target.name]: e.target.value } })
        }
    }

    handleNext = () => {
        const stepOneError = this.stepOneError()
        if (stepOneError) {
            this.setState({ error: stepOneError })
            return
        }
        this.setState({ step: 2, error: "" }, () => this.firstField.current.focus())
    }

    render() {
        const isPercentage = this.state.input.isPercentage === "true"
        const stepOneError = this.stepOneError()
        const stepTwoError = this.stepTwoError()
        const blockingError = this.state.step === 1 ? stepOneError : stepTwoError
        const expirationDate = this.state.input.expirationDate
        const startAfterExpiration = !!(this.state.input.startDate && expirationDate && this.state.input.startDate > expirationDate)
        const agreementTypes = this.props.allAgreementTypes && this.props.allAgreementTypes.edges ? this.props.allAgreementTypes.edges : []
        const agreementOptions = agreementTypes.map((item) => ({ value: item.node.id, label: item.node.name }))
        const carModelOptions = this.props.allCarModels && this.props.allCarModels.edges ? this.props.allCarModels.edges.filter(item => item.node.make && item.node.name).map((item) => ({
            value: item.node.id, label: toTitleCase(item.node.make) + " " + item.node.name + (item.node.series ? " | " + item.node.series : "") + (item.node.groupType ? " | " + item.node.groupType.name : " | No Group Selected")
        })) : []
        const selectedAgreement = agreementTypes.find(item => item.node.id === this.state.input.agreementType)
        const selectedModelCount = this.state.input.carModels ? this.state.input.carModels.length : 0
        const tags = this.state.input.tagNames ? this.state.input.tagNames.split(",").map(tag => tag.trim()).filter(Boolean) : []

        return (
            <Modal isOpen={this.props.isOpen} className="promotion-modal">
                <ModalHeader
                    close={(
                        <button type="button" className="promotion-modal__close" aria-label="Close" onClick={this.props.toggleModal}>
                            ×
                        </button>
                    )}
                >
                    <span className="promotion-modal__title">Create Promotion</span>
                    <span className="promotion-modal__subtitle">Set pricing, eligibility, and availability.</span>
                </ModalHeader>
                <Form onSubmit={this.handleSubmit}>
                    <ModalBody>
                        {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                        {(this.state.loading || this.props.loading) && <Progress animated color="info" value="100" />}

                        <div className="promotion-modal__step" role="status" aria-live="polite">
                            <span>Step {this.state.step} of 2</span>
                            <div aria-hidden="true">
                                <i className={this.state.step >= 1 ? "active" : ""} />
                                <i className={this.state.step >= 2 ? "active" : ""} />
                            </div>
                        </div>

                        <div className="promotion-modal__layout">
                            <div className="promotion-modal__form">
                                {this.state.step === 1 && <>
                                    <section className="promotion-modal__section">
                                        <div className="promotion-modal__section-heading">
                                            <span>1</span>
                                            <div>
                                                <h2>Promotion Basics</h2>
                                                <p className="promotion-modal__subtitle">Name the offer and choose its value type.</p>
                                            </div>
                                        </div>
                                        <div className="promotion-modal__grid">
                                            <FormGroup className="promotion-modal__field promotion-modal__span-8">
                                                <Label for="templateTitle">Promotion Title *</Label>
                                                <Input innerRef={this.firstField} type="text" step="any" name="title" id="templateTitle" placeholder="Summer weekly special" value={this.state.input.title || ""} onChange={(e) => this.handleChange(e)} required />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label>Promotion Type *</Label>
                                                <div className="promotion-modal__segmented">
                                                    <Button
                                                        type="button"
                                                        className={!isPercentage ? "active" : ""}
                                                        aria-pressed={!isPercentage}
                                                        onClick={() => this.setState({ input: { ...this.state.input, isPercentage: "false" } })}
                                                    >
                                                        Flat Price
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className={isPercentage ? "active" : ""}
                                                        aria-pressed={isPercentage}
                                                        onClick={() => this.setState({ input: { ...this.state.input, isPercentage: "true" } })}
                                                    >
                                                        Percentage
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-8">
                                                <div className="promotion-modal__label-row">
                                                    <Label for="tagNames">Tags</Label>
                                                    <span>Optional</span>
                                                </div>
                                                <Input type="text" name="tagNames" id="tagNames" placeholder="promo, seasonal, retention" value={this.state.input.tagNames} onChange={(e) => this.handleChange(e)} />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="visibleToCustomers">Customer Visibility</Label>
                                                <Label className="promotion-modal__visibility" for="visibleToCustomers">
                                                    <span>Visible during booking</span>
                                                    <input
                                                        className="promotion-modal__toggle"
                                                        id="visibleToCustomers"
                                                        name="visibleToCustomers"
                                                        type="checkbox"
                                                        checked={this.state.input.visibleToCustomers}
                                                        onChange={(e) => this.handleChange(e)}
                                                    />
                                                </Label>
                                            </FormGroup>
                                        </div>
                                    </section>

                                    <section className="promotion-modal__section">
                                        <div className="promotion-modal__section-heading">
                                            <span>2</span>
                                            <div>
                                                <h2>Eligibility</h2>
                                                <p className="promotion-modal__subtitle">Choose the agreement and eligible vehicles.</p>
                                            </div>
                                        </div>
                                        <div className="promotion-modal__grid">
                                            <FormGroup className="promotion-modal__field promotion-modal__span-12">
                                                <Label for="agreementType">Agreement Type *</Label>
                                                <Select
                                                    inputId="agreementType"
                                                    className="bos-custom-select"
                                                    classNamePrefix="bos-select"
                                                    isLoading={!(this.props.allAgreementTypes && this.props.allAgreementTypes.edges && this.props.allAgreementTypes.edges.length > 0)}
                                                    options={agreementOptions}
                                                    value={agreementOptions.find(option => option.value === this.state.input.agreementType)}
                                                    placeholder="Select Agreement Type"
                                                    onChange={(agreementType) => this.setState({ input: { ...this.state.input, agreementType: agreementType && agreementType.value ? agreementType.value : null } })}
                                                />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                                <div className="promotion-modal__label-row">
                                                    <Label for="carModel">Car Model</Label>
                                                    <span>Optional</span>
                                                </div>
                                                <Select
                                                    inputId="carModel"
                                                    className="bos-custom-select"
                                                    classNamePrefix="bos-select"
                                                    isLoading={!(this.props.allCarModels && this.props.allCarModels.edges && this.props.allCarModels.edges.length > 0)}
                                                    options={carModelOptions}
                                                    value={carModelOptions.filter(option => this.state.input.carModels && this.state.input.carModels.includes(option.value))}
                                                    isMulti
                                                    placeholder="Select Model"
                                                    onChange={(carModel) => this.setState({ input: { ...this.state.input, carModels: carModel && carModel.length > 0 ? carModel.map(model => model.value) : null } })}
                                                />
                                                <p className="promotion-modal__helper">Empty applies to all models in the agreement branch.</p>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-3">
                                                <div className="promotion-modal__label-row">
                                                    <Label for="carYearMin">Minimum Car Year</Label>
                                                    <span>Optional</span>
                                                </div>
                                                <Input type="number" name="carYearMin" id="carYearMin" placeholder="2020" min={2000} max={3000} value={this.state.input.carYearMin} onChange={(e) => this.handleChange(e)} />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-3">
                                                <div className="promotion-modal__label-row">
                                                    <Label for="carYearMax">Maximum Car Year</Label>
                                                    <span>Optional</span>
                                                </div>
                                                <Input
                                                    invalid={!!(this.state.input.carYearMin && this.state.input.carYearMax && parseInt(this.state.input.carYearMin) > parseInt(this.state.input.carYearMax))}
                                                    type="number" name="carYearMax" id="carYearMax" placeholder="2026" min={this.state.input.carYearMin ? this.state.input.carYearMin : 2000} max={3000}
                                                    value={this.state.input.carYearMax} onChange={(e) => this.handleChange(e)}
                                                />
                                                <FormFeedback>Maximum Car Year must be higher than the Minimum Car Year</FormFeedback>
                                            </FormGroup>
                                        </div>
                                    </section>
                                </>}

                                {this.state.step === 2 && <>
                                    <section className="promotion-modal__section">
                                        <div className="promotion-modal__section-heading">
                                            <span>3</span>
                                            <div>
                                                <h2>Value &amp; Cadence</h2>
                                                <p className="promotion-modal__subtitle">Set the allowed range and recurring cycle.</p>
                                            </div>
                                        </div>
                                        <div className="promotion-modal__grid">
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templatePrice">Default Value {isPercentage ? "(%)" : "($)"} *</Label>
                                                <Input innerRef={this.firstField} type="number" step="any" min="0" name="promoPrice" id="templatePrice" placeholder={isPercentage ? "% Value" : "$ Price"} value={this.state.input.promoPrice} onChange={(e) => this.handleChange(e, true)} required />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templateminimumPromoPrice">Minimum Value {isPercentage ? "(%)" : "($)"} *</Label>
                                                <Input
                                                    valid={this.state.input.promoPrice && this.state.input.minimumPromoPrice && this.state.input.minimumPromoPrice < this.state.input.promoPrice}
                                                    invalid={this.state.input.promoPrice && this.state.input.minimumPromoPrice && this.state.input.minimumPromoPrice > this.state.input.promoPrice}
                                                    type="number" step="any" min="0" name="minimumPromoPrice" id="templateminimumPromoPrice" placeholder={isPercentage ? "% Minimum Value" : "$ Minimum Price"}
                                                    value={this.state.input.minimumPromoPrice} onChange={(e) => this.handleChange(e, true)} required
                                                />
                                                <FormFeedback>Minimum Price must be less than the default Price</FormFeedback>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templatemaximumPromoPrice">Maximum Value {isPercentage ? "(%)" : "($)"} *</Label>
                                                <Input
                                                    valid={this.state.input.promoPrice && this.state.input.maximumPromoPrice && this.state.input.maximumPromoPrice > this.state.input.promoPrice}
                                                    invalid={this.state.input.promoPrice && this.state.input.maximumPromoPrice && this.state.input.maximumPromoPrice < this.state.input.promoPrice}
                                                    type="number" step="any" min="0" name="maximumPromoPrice" id="templatemaximumPromoPrice" placeholder={isPercentage ? "% Maximum Value" : "$ Maximum Price"}
                                                    value={this.state.input.maximumPromoPrice} onChange={(e) => this.handleChange(e, true)} required
                                                />
                                                <FormFeedback>Maximum Price must be higher than the default Price</FormFeedback>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templateCycle">Default Promotion Cycle *</Label>
                                                <Input type="number" step="any" name="cycle" id="templateCycle" placeholder="Cycle" value={this.state.input.cycle} onChange={(e) => this.handleChange(e, true)} required />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templateminCycle">Minimum Cycle *</Label>
                                                <Input
                                                    valid={this.state.input.cycle && this.state.input.minCycle && this.state.input.minCycle < this.state.input.cycle}
                                                    invalid={this.state.input.cycle && this.state.input.minCycle && this.state.input.minCycle > this.state.input.cycle}
                                                    type="number" step="any" name="minCycle" id="templateminCycle" placeholder="Minimum Cycle"
                                                    value={this.state.input.minCycle} onChange={(e) => this.handleChange(e, true)} required
                                                />
                                                <FormFeedback>Minimum Cycle must be less than the default Cycle</FormFeedback>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                                <Label for="templatemaxCycle">Maximum Cycle *</Label>
                                                <Input
                                                    valid={this.state.input.cycle && this.state.input.maxCycle && this.state.input.maxCycle > this.state.input.cycle}
                                                    invalid={this.state.input.cycle && this.state.input.maxCycle && this.state.input.maxCycle < this.state.input.cycle}
                                                    type="number" step="any" name="maxCycle" id="templatemaxCycle" placeholder="Maximum Cycle"
                                                    value={this.state.input.maxCycle} onChange={(e) => this.handleChange(e, true)} required
                                                />
                                                <FormFeedback>Maximum Cycle must be higher than the default Cycle</FormFeedback>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                                <Label for="templateInterval">Interval *</Label>
                                                <Input type="number" name="interval" id="templateInterval" placeholder="Interval" min="1" max="9" value={this.state.input.interval} onChange={(e) => this.handleChange(e)} required />
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                                <Label for="templateIntervalUnit">Interval Unit *</Label>
                                                <Select
                                                    inputId="templateIntervalUnit"
                                                    className="bos-custom-select"
                                                    classNamePrefix="bos-select"
                                                    options={IntervalOptions}
                                                    value={IntervalOptions.find(option => option.value === this.state.input.intervalUnit)}
                                                    onChange={(intervalUnit) => this.setState({ input: { ...this.state.input, intervalUnit: intervalUnit.value } })}
                                                    placeholder="Select Interval Unit"
                                                />
                                            </FormGroup>
                                        </div>
                                    </section>

                                    <section className="promotion-modal__section">
                                        <div className="promotion-modal__section-heading">
                                            <span>4</span>
                                            <div>
                                                <h2>Availability</h2>
                                                <p className="promotion-modal__subtitle">Choose when the promotion can be applied.</p>
                                            </div>
                                        </div>
                                        <div className="promotion-modal__grid">
                                            <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                                <Label for="startDate">Start Date *</Label>
                                                <Input
                                                    invalid={startAfterExpiration}
                                                    name="startDate" id="startDate" placeholder="Start Date" type="date" min={moment().tz("America/New_York").format(moment.HTML5_FMT.DATE)}
                                                    value={this.state.input.startDate || ""} onChange={(e) => this.handleChange(e)} required
                                                />
                                                <FormFeedback>Start Date must be on or before the Expiration Date</FormFeedback>
                                            </FormGroup>
                                            <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                                <Label for="expirationDate">Expiration Date *</Label>
                                                <Input
                                                    valid={!!expirationDate && !startAfterExpiration && isSunday(expirationDate)}
                                                    invalid={!!expirationDate && (startAfterExpiration || !isSunday(expirationDate))}
                                                    name="expirationDate" id="expirationDate" placeholder="Expiration Date" type="date" min={moment().tz("America/New_York").format(moment.HTML5_FMT.DATE)}
                                                    value={expirationDate || ""} onChange={(e) => this.handleChange(e)} required
                                                />
                                                {expirationDate && !isSunday(expirationDate)
                                                    ? <FormFeedback>Expiration Date can only be a Sunday</FormFeedback>
                                                    : <FormFeedback>Expiration Date must be on or after the Start Date</FormFeedback>}
                                                <p className="promotion-modal__helper promotion-modal__helper--warning">Expiration must fall on a Sunday.</p>
                                            </FormGroup>
                                        </div>
                                    </section>
                                </>}
                            </div>

                            <aside className="promotion-modal__summary" aria-label="Promotion summary">
                                <h2>Promotion Summary</h2>
                                <p className="promotion-modal__subtitle">Updates as values are entered.</p>
                                <div className="promotion-modal__summary-card">
                                    <div className="promotion-modal__summary-value">
                                        <small>{this.state.input.title || "Default Discount"}</small>
                                        <strong>
                                            {!isPercentage && "$"}
                                            {this.state.input.promoPrice || "—"}
                                            <span>{isPercentage ? "% percentage" : " flat price"}</span>
                                        </strong>
                                    </div>
                                    <dl>
                                        <div>
                                            <dt>Agreement</dt>
                                            <dd>{selectedAgreement ? selectedAgreement.node.name : "Not selected"}</dd>
                                        </div>
                                        <div>
                                            <dt>Models</dt>
                                            <dd>{selectedModelCount ? `${selectedModelCount} selected` : "All branch models"}</dd>
                                        </div>
                                        <div>
                                            <dt>Schedule</dt>
                                            <dd>{this.state.input.startDate && this.state.input.expirationDate ? `${this.state.input.startDate} – ${this.state.input.expirationDate}` : "Not scheduled"}</dd>
                                        </div>
                                        <div>
                                            <dt>Cadence</dt>
                                            <dd>{this.state.input.intervalUnit ? `Every ${this.state.input.interval} ${this.state.input.intervalUnit.toLowerCase()} · cycle ${this.state.input.cycle || "—"}` : "Not selected"}</dd>
                                        </div>
                                    </dl>
                                </div>
                                {tags.length > 0 && (
                                    <div className="promotion-modal__tags" aria-label="Promotion tags">
                                        {tags.map((tag, index) => <span key={`${tag}-${index}`}>{tag}</span>)}
                                    </div>
                                )}
                            </aside>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="promotion-modal__footer-note">
                            {this.state.step === 2 && (
                                <label className="promotion-modal__create-another">
                                    <input className="promotion-modal__checkbox" name="createAnother" type="checkbox" checked={this.state.createAnother} onChange={(e) => this.setState({ createAnother: e.target.checked })} />
                                    Create another after saving
                                </label>
                            )}
                            {blockingError && <span className="promotion-modal__blocked" role="status">{blockingError}</span>}
                        </div>
                        <div className="promotion-modal__actions">
                            <Button color="secondary" type="button" onClick={this.props.toggleModal}>Cancel</Button>
                            {this.state.step === 1 ? (
                                <Button color="primary" type="button" onClick={this.handleNext} disabled={!!stepOneError}>Next</Button>
                            ) : (
                                <>
                                    <Button color="secondary" type="button" onClick={() => this.setState({ step: 1, error: "" }, () => this.firstField.current.focus())}>Back</Button>
                                    <Button color="primary" type="submit" disabled={!!stepOneError || !!stepTwoError || this.state.loading}>Create Promo</Button>
                                </>
                            )}
                        </div>
                    </ModalFooter>
                </Form>
            </Modal >
        )
    }
}

export default graphql(AllCarModels, {
    options: { fetchPolicy: "network-only" },
    props: ({ data }) => {
        if (!data) {
            console.error('AllCarModels data is undefined')
            return { loading: true, allCarModels: [] }
        }
        const { loading, allCarModels } = data
        return { loading, allCarModels }
    }
})(
    graphql(AllAgreementTypes, {
        options: { fetchPolicy: "network-only" },
        props: ({ data }) => {
            if (!data) {
                console.error('AllAgreementTypes data is undefined')
                return { loading: true, allAgreementTypes: [] }
            }
            const { loading, allAgreementTypes } = data
            return { loading, allAgreementTypes }
        }
    })(withApolloClient(
        AddPromotionModal)
    )
)
